import { useLazyQuery } from '@apollo/react-hooks';
import React, { useEffect, useState } from 'react';
import { useModelFilter } from './ModelFilterContext';
import { MODEL_FILTER_KEYS, MODEL_FILTER_TYPES } from './constants';
import modelFilterQuery from './modelFilter.gql';

/**
 * Custom hook to manage model filter data and related operations
 */
export function useModelFilterSelectionData() {
  const { data: liveData, setData: setLiveData, getItems } = useModelFilter();
  const dataLoadedRef = React.useRef(false);
  const [category, setCategory] = useState(null);

  const [updateCategory, { loading, error, data: _category }] = useLazyQuery(
    modelFilterQuery,
    {
      fetchPolicy: 'cache-and-network'
    }
  );

  useEffect(() => {
    if (!loading && !error && _category) {
      setCategory(_category);
    }
  }, [loading, error, _category]);

  function clear() {
    setLiveData({
      type: null,
      brand: null,
      model: null
    });
    refetchCategory([]);
  }

  function loadInitialData() {
    if (!dataLoadedRef.current) {
      dataLoadedRef.current = true;
      refetchCategory(getItems(liveData));
    }
  }

  function refetchCategory(filterValues) {
    updateCategory({
      variables: {
        modelCategory: 2810,
        activeFilter: {
          multiListFilters: [
            {
              id: 'modellfilter',
              values: filterValues
            }
          ]
        }
      }
    });
  }

  const filters = category?.category?.products?.filters?.[0]?.lists;

  const getFilterItems = filterName => {
    return filters?.find(filter => filter.name === filterName)?.items;
  };

  const toggleFilterItem = (filterName, itemId, itemText) => {
    setLiveData(currentFilters => {
      const updatedFilters = { ...currentFilters };
      const filterKey = MODEL_FILTER_KEYS[filterName];

      // Check if we're toggling off the item (it's already selected)
      const isTogglingOff = updatedFilters[filterKey]?.some(
        item => item.id === itemId
      );

      // Handle the toggle for the current filter
      updatedFilters[filterKey] = isTogglingOff
        ? null
        : [{ id: itemId, text: itemText }];

      // Handle cascading resets based on filter hierarchy
      if (filterName === MODEL_FILTER_TYPES.TYPE) {
        updatedFilters.brand = null;
        updatedFilters.model = null;
      } else if (filterName === MODEL_FILTER_TYPES.BRAND) {
        updatedFilters.model = null;
      }

      const newFilterValues = getItems(updatedFilters);

      setTimeout(() => {
        refetchCategory(newFilterValues);
      }, 0);

      return updatedFilters;
    });
  };

  return {
    loading,
    error,
    refetchCategory,
    loadInitialData,
    modelFilters: liveData,
    getFilterItems,
    toggleFilterItem,
    clear
  };
}
